import React, { useState, useEffect } from 'react';
import { useColorPalette } from '../../hooks/useColorPalette';
import DynamicIcon from '../DynamicIcon';
import Pagination from './Pagination';
import EmptyState from './EmptyState';
import LoadingState from './LoadingState';
import './DataTable.css';

/**
 * Componente DataTable avanzato con supporto per ordinamento, paginazione e ricerca
 * 
 * @param {Object} props
 * @param {Array} props.columns - Definizioni delle colonne (array di oggetti)
 * @param {Array} props.data - Dati da visualizzare o null/undefined se i dati sono ancora in caricamento
 * @param {Function} props.onPageChange - Callback chiamata quando cambia la pagina (start, length) => {}
 * @param {Function} props.onSortChange - Callback chiamata quando cambia ordinamento (columnId, direction) => {}
 * @param {Boolean} props.loading - Flag che indica se i dati stanno caricando
 * @param {Number} props.totalRecords - Numero totale di record disponibili
 * @param {Number} props.pageSize - Numero di elementi per pagina (default: 10)
 * @param {String} props.className - Classi CSS aggiuntive
 * @param {String} props.emptyMessage - Messaggio da mostrare quando non ci sono dati
 * @param {Boolean} props.isResponsive - Se true, rende la tabella responsive (default: true)
 * @param {String} props.tableId - ID univoco per la tabella
 * @param {Function} props.renderCustomRow - Funzione per renderizzare una riga personalizzata
 * @param {Boolean} props.hasSearch - Se true, mostra la barra di ricerca (default: false)
 * @param {Function} props.onSearch - Callback chiamata quando cambia la ricerca
 * @param {String} props.initialSearchTerm - Termine di ricerca iniziale
 * @param {Number} props.minSearchLength - Lunghezza minima per attivare la ricerca (default: 2)
 */
const DataTable = ({
    columns = [],
    data = [],
    onPageChange = () => {},
    onSortChange = null,
    loading = false,
    totalRecords = 0,
    pageSize = 10,
    className = '',
    emptyMessage = 'Nessun dato disponibile',
    isResponsive = true,
    tableId = 'data-table',
    renderCustomRow = null,
    hasSearch = false,
    onSearch = null,
    initialSearchTerm = '',
    minSearchLength = 2
}) => {
    const palette = useColorPalette();
    const [currentPage, setCurrentPage] = useState(0);
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
    const [searchTimeout, setSearchTimeout] = useState(null);

    // Configura gli stili in base alla palette
    const tableStyles = {
        header: {
            backgroundColor: palette.tableHeaderBg || '#f3f4f6',
            color: palette.tableHeaderText || '#1f2937'
        },
        evenRow: {
            backgroundColor: '#ffffff',  // Bianco per le righe pari
            color: palette.textOnTableRowLight || '#111827'
        },
        oddRow: {
            backgroundColor: palette.tableRowDark || '#c3c0b6',  // Usa tableRowDark dal colorSchema
            color: palette.textOnTableRowDark || '#111827'
        },
        hover: {
            backgroundColor: palette.highlightBg || '#e5e7eb',
            color: palette.textOnHighlight || '#111827'
        },
        border: palette.borderColor || '#e5e7eb',
        shadow: palette.shadowColor || 'rgba(0, 0, 0, 0.1)'
    };

    // Gestione cambio pagina
    const handlePageChange = (newPage) => {
        console.log('[DEBUG PAGINATION] DataTable handlePageChange:', { 
            newPage, 
            pageIndex: newPage, // Indice pagina (0-based)
            pageSize,
            totalPages: Math.ceil(totalRecords / pageSize)
        });
        
        setCurrentPage(newPage);
        
        // Passiamo SOLO l'indice della pagina, non l'offset moltiplicato
        // Il calcolo dell'offset effettivo verrà gestito dal server
        onPageChange(newPage, pageSize);
    };

    // Gestione ordinamento
    const handleSort = (columnId) => {
        if (!onSortChange) return;
        
        let newDirection = 'asc';
        if (sortColumn === columnId) {
            newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        }
        
        setSortColumn(columnId);
        setSortDirection(newDirection);
        onSortChange(columnId, newDirection);
    };

    // Gestione ricerca
    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        
        // Debounce per evitare troppe chiamate durante la digitazione
        if (searchTimeout) clearTimeout(searchTimeout);
        
        const timeout = setTimeout(() => {
            if (!onSearch) return;
            if (value === '' || value.length >= minSearchLength) {
                onSearch(value);
            }
        }, 300);
        
        setSearchTimeout(timeout);
    };

    // Pulizia del timeout alla smontaggio del componente
    useEffect(() => {
        return () => {
            if (searchTimeout) clearTimeout(searchTimeout);
        };
    }, [searchTimeout]);

    // Gestione reset della ricerca
    const handleResetSearch = () => {
        setSearchTerm('');
        if (onSearch) onSearch('');
    };

    return (
        <div className={`data-table-container ${className}`} id={tableId}>
            {/* Wrapper per tabella responsive */}
            <div className={`${isResponsive ? 'overflow-x-auto' : ''} w-full bg-white rounded-lg shadow`}>
                <div className="data-table-wrapper" style={{ borderColor: tableStyles.border }}>
                    {/* Header della tabella */}
                    <div className="data-table-header" style={tableStyles.header}>
                        {columns.map((column, index) => (
                            <div 
                                key={`col-${index}`}
                                className={`data-table-header-cell ${column.sortable ? 'sortable' : ''} ${column.className || ''}`}
                                style={{ width: column.width || 'auto' }}
                                onClick={column.sortable && onSortChange ? () => handleSort(column.id) : undefined}
                            >
                                <div className="flex items-center justify-center" style={{ textTransform: 'uppercase' }}>
                                    {column.title}
                                    {column.sortable && onSortChange && (
                                        <span className="sort-icon ml-2">
                                            {sortColumn === column.id ? (
                                                <DynamicIcon 
                                                    iconClass={`fa-solid fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`} 
                                                />
                                            ) : (
                                                <DynamicIcon iconClass="fa-solid fa-sort" />
                                            )}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Corpo della tabella */}
                    <div className="data-table-body">
                        {loading ? (
                            <LoadingState />
                        ) : data.length === 0 ? (
                            <EmptyState message={emptyMessage} />
                        ) : (
                            data.map((item, rowIndex) => {
                                const isEven = rowIndex % 2 === 0;
                                
                                // Se è stata fornita una funzione di rendering personalizzata, usala
                                if (renderCustomRow) {
                                    // Wrappa il risultato di renderCustomRow in un React Fragment con chiave
                                    // per evitare il warning "Each child in a list should have a unique key prop"
                                    return (
                                        <React.Fragment key={`custom-row-${rowIndex}`}>
                                            {renderCustomRow(item, rowIndex, isEven, tableStyles)}
                                        </React.Fragment>
                                    );
                                }
                                
                                return (
                                    <div 
                                        key={`row-${rowIndex}`}
                                        className="data-table-row"
                                        style={{
                                            backgroundColor: isEven ? tableStyles.evenRow.backgroundColor : tableStyles.oddRow.backgroundColor,
                                            color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color,
                                        }}
                                    >
                                        {columns.map((column, colIndex) => (
                                            <div 
                                                key={`cell-${rowIndex}-${colIndex}`}
                                                className={`data-table-cell ${column.className || ''}`}
                                                style={{ width: column.width || 'auto' }}
                                            >
                                                {column.render 
                                                    ? column.render(item, rowIndex) 
                                                    : item[column.id] || '-'}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
                
                {/* Paginazione */}
                {!loading && totalRecords > 0 && (
                    <div className="data-table-pagination">
                        <Pagination 
                            currentPage={currentPage}
                            totalPages={Math.ceil(totalRecords / pageSize)}
                            onPageChange={handlePageChange}
                            totalRecords={totalRecords}
                            pageSize={pageSize}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default DataTable;
